import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit, Delete, Close, Add } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Price {
  externalId: string;
  priceCategoryId: string;
  solutionId: string;
  price: number;
  solution: {
    externalId: string;
    name: string;
    alias: string;
  };
  priceCategory?: {
    externalId: string;
    solutionCategoryId: string;
    priceMin: number;
    priceMax: number;
    solutionCategory: {
      externalId: string;
      name: string;
    };
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

const PricesPage: React.FC = () => {
  const { t } = useTranslation();
  const [prices, setPrices] = useState<Price[]>([]);
  const [priceCategories, setPriceCategories] = useState<
    Array<{
      externalId: string;
      solutionCategoryId: string;
      priceMin: number;
      priceMax: number;
      solutionCategory: {
        externalId: string;
        name: string;
      };
      createdAt: string;
      updatedAt: string;
    }>
  >([]);
  const [solutions, setSolutions] = useState<
    Array<{
      externalId: string;
      name: string;
      categoryId: string;
    }>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingPrice, setDeletingPrice] = useState<Price | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingPrice, setEditingPrice] = useState<Price | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState({
    solutionId: '',
    price: '',
  });
  const [addFormData, setAddFormData] = useState({
    priceCategoryId: '',
    solutionId: '',
    price: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [pricesResponse, categoriesResponse, solutionsResponse] =
          await Promise.all([
            apiService.getPrices(),
            apiService.getPriceCategories(),
            apiService.getSolutions(),
          ]);

        setPrices(pricesResponse.items);
        setPriceCategories(categoriesResponse.items);
        setSolutions(solutionsResponse.items);
      } catch (err: any) {
        console.error('Error loading prices:', err);
        setError(err.message || t('prices.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const handleDeleteClick = (price: Price) => {
    setDeletingPrice(price);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingPrice(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPrice) return;

    try {
      setDeleting(true);
      await apiService.deletePrice(deletingPrice.externalId);

      // Remove price from list
      setPrices(prev =>
        prev.filter(p => p.externalId !== deletingPrice.externalId)
      );

      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting price:', err);
      setError(err.message || t('prices.errors.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (price: Price) => {
    setEditingPrice(price);
    setEditFormData({
      solutionId: price.solutionId,
      price: price.price.toString(),
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingPrice(null);
    setEditFormData({
      solutionId: '',
      price: '',
    });
  };

  const handleEditInputChange =
    (field: keyof typeof editFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSaveEdit = async () => {
    if (!editingPrice) return;

    // Validate required fields
    if (!editFormData.solutionId || !editFormData.price) {
      setError(t('prices.errors.fillRequiredFields'));
      return;
    }

    const price = parseFloat(editFormData.price);

    if (isNaN(price)) {
      setError(t('prices.errors.invalidNumber'));
      return;
    }

    if (price < 0) {
      setError(t('prices.errors.mustBePositive'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updatedPrice = await apiService.updatePrice(
        editingPrice.externalId,
        {
          solutionId: editFormData.solutionId,
          price,
        }
      );

      // Update price in list
      setPrices(prev =>
        prev.map(p =>
          p.externalId === editingPrice.externalId ? updatedPrice : p
        )
      );

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating price:', err);
      setError(err.message || t('prices.errors.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      priceCategoryId: '',
      solutionId: '',
      price: '',
    });
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      priceCategoryId: '',
      solutionId: '',
      price: '',
    });
  };

  const handleAddInputChange =
    (field: keyof typeof addFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleAddSelectChange =
    (field: 'priceCategoryId' | 'solutionId') => (event: any) => {
      setAddFormData(prev => {
        const newData = {
          ...prev,
          [field]: event.target.value,
        };
        // If category changed, reset solution selection
        if (field === 'priceCategoryId') {
          newData.solutionId = '';
        }
        return newData;
      });
    };

  const handleSaveAdd = async () => {
    // Validate required fields
    if (
      !addFormData.priceCategoryId ||
      !addFormData.solutionId ||
      !addFormData.price
    ) {
      setError(t('prices.errors.fillRequiredFields'));
      return;
    }

    const price = parseFloat(addFormData.price);

    if (isNaN(price)) {
      setError(t('prices.errors.invalidNumber'));
      return;
    }

    if (price < 0) {
      setError(t('prices.errors.mustBePositive'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const newPrice = await apiService.createPrice({
        priceCategoryId: addFormData.priceCategoryId,
        solutionId: addFormData.solutionId,
        price,
      });

      // Add price to list
      setPrices(prev => [...prev, newPrice]);

      handleCloseAddModal();
    } catch (err: any) {
      console.error('Error creating price:', err);
      setError(err.message || t('prices.errors.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('prices.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddClick}
        >
          {t('prices.addPrice')}
        </Button>
      </Box>

      {error && (
        <Alert variant="filled" severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('prices.table.solution')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('prices.table.price')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('prices.table.createdAt')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('prices.table.updatedAt')}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6">{t('prices.table.actions')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('prices.noPricesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  prices.map(price => (
                    <TableRow key={price.externalId} hover>
                      <TableCell>{price.solution.name}</TableCell>
                      <TableCell>{price.price} {t('prices.credits')}</TableCell>
                      <TableCell>{formatDate(price.createdAt)}</TableCell>
                      <TableCell>{formatDate(price.updatedAt)}</TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title={t('prices.tooltips.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(price)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('prices.tooltips.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(price)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('prices.modals.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('prices.modals.delete.confirm')} "
            {deletingPrice?.solution.name}" ({deletingPrice?.price} {t('prices.credits')})?
            {t('prices.modals.delete.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            color="secondary"
            disabled={deleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('prices.modals.delete.deleting') : t('prices.modals.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Price Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('prices.modals.edit.title')}</Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('prices.modals.edit.price')}</InputLabel>
              <OutlinedInput
                type="number"
                value={editFormData.price}
                onChange={handleEditInputChange('price')}
                label={t('prices.modals.edit.price')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseEditModal}
            color="secondary"
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveEdit}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('prices.modals.edit.saving') : t('prices.modals.edit.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Price Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('prices.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseAddModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('prices.modals.add.priceCategory')}</InputLabel>
              <Select
                label={t('prices.modals.add.priceCategory')}
                value={addFormData.priceCategoryId}
                onChange={handleAddSelectChange('priceCategoryId')}
              >
                {priceCategories.map(category => (
                  <MenuItem
                    key={category.externalId}
                    value={category.externalId}
                  >
                    {category.solutionCategory.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('prices.modals.add.solution')}</InputLabel>
              <Select
                label={t('prices.modals.add.solution')}
                value={addFormData.solutionId}
                onChange={handleAddSelectChange('solutionId')}
                disabled={!addFormData.priceCategoryId}
              >
                {(() => {
                  const selectedCategory = priceCategories.find(
                    cat => cat.externalId === addFormData.priceCategoryId,
                  );
                  if (!selectedCategory) return [];

                  return solutions
                    .filter(
                      solution =>
                        solution.categoryId ===
                        selectedCategory.solutionCategoryId,
                    )
                    .map(solution => (
                      <MenuItem
                        key={solution.externalId}
                        value={solution.externalId}
                      >
                        {solution.name}
                      </MenuItem>
                    ));
                })()}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('prices.modals.add.price')}</InputLabel>
              <OutlinedInput
                type="number"
                value={addFormData.price}
                onChange={handleAddInputChange('price')}
                label={t('prices.modals.add.price')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseAddModal}
            color="secondary"
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('prices.modals.add.creating') : t('prices.modals.add.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricesPage;
